import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "outline" | "ghost" | "link";
type Size = "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium font-body whitespace-nowrap transition-colors transition-shadow duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 active:scale-[0.98]";

const variants: Record<Variant, string> = {
  primary:
    "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 hover:shadow-md",
  secondary:
    "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/90 hover:shadow-md",
  outline:
    "border border-border bg-background text-foreground hover:bg-muted hover:border-primary/30",
  ghost: "text-foreground hover:bg-muted",
  link: "text-secondary underline-offset-4 hover:underline px-0 h-auto",
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-3 text-sm",
  md: "h-11 px-5 text-sm",
  lg: "h-12 px-6 text-base",
};

interface CommonProps {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
}

type ButtonAsButton = CommonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & { as?: "button" };

type ButtonAsLink = CommonProps & {
  as: "link";
  to: string;
  "data-testid"?: string;
};

type ButtonAsAnchor = CommonProps & {
  as: "a";
  href: string;
  target?: string;
  rel?: string;
  "data-testid"?: string;
};

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

/** Flexible button that can render as a <button>, router <Link>, or <a>. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", size = "md", className, children, ...props }, ref) => {
    const classes = cn(
      base,
      variants[variant],
      variant !== "link" && sizes[size],
      className,
    );

    if (props.as === "link") {
      const { as, to, ...rest } = props;
      return (
        <Link to={to} className={classes} {...rest}>
          {children}
        </Link>
      );
    }

    if (props.as === "a") {
      const { as, ...rest } = props;
      return (
        <a className={classes} {...rest}>
          {children}
        </a>
      );
    }

    const { as, ...rest } = props as ButtonAsButton;
    return (
      <button ref={ref} className={classes} {...rest}>
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";
